import type { JSX, ComponentType } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  CloudOff,
  LogIn,
  ShieldOff,
  WifiOff,
  type LucideProps,
} from 'lucide-react';

// ── Pages d'erreur dédiées /error/:code (UX-03) ─────────────────────────────
// Codes gérés : 401, 403, 404, 500, 503, network.
// Fallback : 500. Design conforme DS (bg-white, rounded-xl, shadow-sm,
// icône dans bg-brand-50, touch-manipulation sur CTA mobile).

type NavigateFn = ReturnType<typeof useNavigate>;

interface ErrorAction {
  label: string;
  onClick: (navigate: NavigateFn) => void;
}

interface ErrorConfig {
  Icon: ComponentType<LucideProps>;
  title: string;
  description: string;
  primaryAction: ErrorAction;
  secondaryAction?: ErrorAction;
}

const CONFIGS: Record<string, ErrorConfig> = {
  '401': {
    Icon: LogIn,
    title: 'Connexion requise',
    description:
      'Votre session a expiré. Veuillez vous reconnecter pour continuer.',
    primaryAction: {
      label: 'Se reconnecter',
      onClick: () => window.location.assign('/'),
    },
  },
  '403': {
    Icon: ShieldOff,
    title: 'Accès refusé',
    description:
      "Vous n'avez pas les permissions nécessaires pour cette page. Contactez un administrateur si vous pensez que c'est une erreur.",
    primaryAction: {
      label: "Retour à l'accueil",
      onClick: (navigate) => navigate('/'),
    },
  },
  '404': {
    Icon: AlertTriangle,
    title: 'Page introuvable',
    description:
      "La page que vous cherchez n'existe pas ou a été déplacée.",
    primaryAction: {
      label: "Retour à l'accueil",
      onClick: (navigate) => navigate('/'),
    },
  },
  '500': {
    Icon: AlertTriangle,
    title: 'Erreur serveur',
    description:
      'Une erreur inattendue est survenue côté serveur. Nos équipes ont été notifiées.',
    primaryAction: {
      label: 'Réessayer',
      onClick: () => window.location.reload(),
    },
    secondaryAction: {
      label: "Retour à l'accueil",
      onClick: (navigate) => navigate('/'),
    },
  },
  '503': {
    Icon: CloudOff,
    title: 'Service temporairement indisponible',
    description:
      "L'application est en maintenance ou subit une charge exceptionnelle. Réessayez dans quelques minutes.",
    primaryAction: {
      label: 'Réessayer',
      onClick: () => window.location.reload(),
    },
  },
  network: {
    Icon: WifiOff,
    title: 'Pas de connexion',
    description: 'Vérifiez votre connexion Internet puis réessayez.',
    primaryAction: {
      label: 'Réessayer',
      onClick: () => window.location.reload(),
    },
  },
};

export function GsErrorPage(): JSX.Element {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const config = CONFIGS[code ?? ''] ?? CONFIGS['500'];
  const { Icon } = config;

  return (
    <div className="min-h-screen flex items-center justify-center bg-page dark:bg-slate-900 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-8 max-w-md w-full text-center">
        <div
          className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-50 text-brand-500 mb-4"
          aria-hidden
        >
          <Icon className="h-8 w-8" />
        </div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          {config.title}
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
          {config.description}
        </p>
        {code && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 font-mono">
            Code : {code}
          </p>
        )}
        <div className="flex flex-col gap-2 mt-6">
          <button
            type="button"
            onClick={() => config.primaryAction.onClick(navigate)}
            className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white font-medium touch-manipulation transition-colors"
          >
            {config.primaryAction.label}
          </button>
          {config.secondaryAction && (
            <button
              type="button"
              onClick={() => config.secondaryAction!.onClick(navigate)}
              className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-medium touch-manipulation transition-colors"
            >
              {config.secondaryAction.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
